import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Drawer, Input, Modal, Popconfirm } from 'antd'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { BillDetail } from './bill-detail.tsx'
import { MoneyDetailContent } from './money-detail-content.tsx'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

export function MoneyDetail({ id, canManage, onClose }: { id: number; canManage: boolean; onClose: () => void }) {
  const access = useAccess()
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message } = App.useApp(),
    q = useQuery(trpc.bill.moneyDetail.queryOptions({ transactionId: id }))
  const confirm = useMutation(trpc.bill.confirmMoney.mutationOptions()),
    voidMoney = useMutation(trpc.bill.voidMoney.mutationOptions()),
    [voiding, setVoiding] = useState(false),
    [reason, setReason] = useState(''),
    [billId, setBillId] = useState<number>()
  const refresh = async () => {
    await refreshMoneyQueries(cache, trpc)
  }
  const [open, setOpen] = useState(true)
  const m = q.data
  const permissionKind = m?.kind === 'receipt' ? 'receipt' : m?.kind === 'payment' ? 'payment' : 'refund'
  const canConfirm = access.can(`bill.${permissionKind}.confirm`)
  const canVoid = access.can(`bill.${permissionKind}.void`)
  return (
    <>
      <Drawer
        open={open}
        title={
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>资金流水</span>
            {m && <span className="text-content-muted text-xs font-normal tabular-nums">{m.transactionNo}</span>}
          </div>
        }
        closable={{ placement: 'end' }}
        size={DRAWER_SIZE}
        onClose={() => setOpen(false)}
        afterOpenChange={(visible) => {
          if (!visible) {
            onClose()
          }
        }}
        classNames={{
          body: 'bg-page p-4! sm:p-6!',
          header: 'px-4! py-4! sm:px-6!',
          footer: 'px-4! py-3! sm:px-6!'
        }}
        loading={q.isPending}
        footer={
          m && (
            <div className="flex min-h-10 flex-wrap items-center justify-between gap-3">
              <div>
                {canVoid && m.status !== 'voided' && (
                  <Button size="medium" danger type="text" onClick={() => setVoiding(true)}>
                    作废错误登记
                  </Button>
                )}
              </div>
              <div className="ml-auto flex items-center gap-3">
                <Button size="medium" onClick={() => setOpen(false)}>
                  关闭
                </Button>
                {canConfirm && m.status === 'pending' && (
                  <Popconfirm
                    title="重新核对余额并确认入账？"
                    onConfirm={async () => {
                      try {
                        await confirm.mutateAsync({ transactionId: id })
                        await refresh()
                      } catch (e) {
                        void message.error(e instanceof Error ? e.message : '确认失败')
                      }
                    }}
                  >
                    <Button size="medium" type="primary" loading={confirm.isPending}>
                      确认入账
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </div>
          )
        }
      >
        {q.isError && <Alert type="error" title={q.error.message} />}
        {m && <MoneyDetailContent money={m} onBill={access.can('bill.read') ? setBillId : undefined} />}
        {/* 子抽屉保留父层上下文，复用订单查看账单时的推开与回退动画。 */}
        {billId && (
          <BillDetail key={billId} billId={billId} canManage={canManage} onClose={() => setBillId(undefined)} />
        )}
      </Drawer>
      <Modal
        open={voiding}
        title="作废资金流水"
        onCancel={() => setVoiding(false)}
        confirmLoading={voidMoney.isPending}
        okButtonProps={{ danger: true, disabled: !reason.trim() || !canVoid }}
        onOk={async () => {
          try {
            await voidMoney.mutateAsync({ transactionId: id, reason })
            await refresh()
            setVoiding(false)
          } catch (e) {
            void message.error(e instanceof Error ? e.message : '作废失败')
          }
        }}
      >
        <Input.TextArea aria-label="作废原因" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </>
  )
}
